import { AssessmentLibrary } from './AssessmentLibrary';
import { ScreeningQuestionBank } from './RecruitingWorkflows';
// pages/hr/recruiting/CampaignEditPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  CircularProgress,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBackOutlined,
  ArrowUp as ArrowUpward,
  ArrowDown as ArrowDownward,
  Briefcase as WorkOutlined,
  Calendar as CalendarOutlined,
  ChatCircle as SmsOutlined,
  FloppyDisk as SaveOutlined,
  Gauge as ScoreOutlined,
  ListChecks as QuestionsOutlined,
  Plus as AddOutlined,
  Trash as DeleteOutlined,
} from '@phosphor-icons/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHelpButton } from '../../../components/knowledge-base/PageHelpButton';
import {
  useRecruitingCampaign,
  useCampaignQuestions,
  useUpdateCampaignQuestions,
  useUpdateRecruitingCampaign,
} from '../../../hooks/useHr';
import { schedulingApi } from '../../../lib/apiScheduling';
import type {
  CampaignStatus,
  QuestionType,
  ScoringRubric,
  UpdateCampaignRequest,
} from '../../../types/hr';
import toast from 'react-hot-toast';

const statusColors: Record<CampaignStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  closed: 'danger',
};

const statusLabels: Record<CampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  closed: 'Closed',
};

const questionTypeLabels: Record<QuestionType, string> = {
  text: 'Free text',
  yes_no: 'Yes / No',
  multiple_choice: 'Multiple choice',
  number: 'Number',
};

/**
 * Local draft of a qualification question. `key` keeps React rows stable while a
 * brand-new question has no server id yet; `id` is only present for questions the
 * bulk PUT should keep instead of recreate.
 */
interface QuestionDraft {
  key: string;
  id?: string;
  question_text: string;
  question_type: QuestionType;
  options: string[];
  is_knockout: boolean;
  knockout_answer: string;
}

/** The rubric the scoring service falls back to when the campaign leaves it empty. */
const DEFAULT_RUBRIC: RubricRow[] = [
  { key: 'r-default-0', name: 'Relevant experience', weight: 3, description: 'Previous roofing, construction, or related trade experience' },
  { key: 'r-default-1', name: 'Accurate commitments', weight: 2, description: 'Explicit examples of keeping customers informed' },
  { key: 'r-default-2', name: 'Safe judgment', weight: 2, description: 'Explicit examples of following job-related safety procedures' },
  { key: 'r-default-3', name: 'Communication skills', weight: 1, description: 'Clear communication, customer service background' },
  { key: 'r-default-4', name: 'Record accuracy', weight: 1, description: 'Demonstrated factual customer notes and follow-through' },
];

interface RubricRow {
  key: string;
  name: string;
  weight: number;
  description: string;
}

const newKey = () => `k-${Math.random().toString(36).slice(2)}`;

/** Lets other pages deep-link straight to the tab that fixes their problem. */
const tabSlugs = ['job-post', 'questions', 'messaging', 'scheduling', 'scoring', 'assessment'];

/** The rubric is stored as a JSON object, so flatten it into editable rows. */
const rubricToRows = (rubric?: ScoringRubric): RubricRow[] =>
  Object.entries(rubric || {}).map(([name, value]) => ({
    key: newKey(),
    name,
    // Older rubrics stored a plain description string instead of {weight, description}.
    weight: typeof value === 'object' ? value.weight : 1,
    description: typeof value === 'object' ? value.description : value,
  }));

const rowsToRubric = (rows: RubricRow[]): ScoringRubric =>
  rows.reduce<ScoringRubric>((acc, row) => {
    const name = row.name.trim();
    if (name) {
      acc[name] = { weight: row.weight, description: row.description };
    }
    return acc;
  }, {});

export const CampaignEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const campaignId = id || '';

  const { data: campaign, isLoading } = useRecruitingCampaign(campaignId);
  const { data: questions, isLoading: questionsLoading } = useCampaignQuestions(campaignId);
  const updateCampaign = useUpdateRecruitingCampaign();
  const updateQuestions = useUpdateCampaignQuestions();

  const { data: teams = [] } = useQuery({
    queryKey: ['schedulingTeams'],
    queryFn: schedulingApi.getTeams,
  });

  const [activeTab, setActiveTab] = useState(() =>
    Math.max(tabSlugs.indexOf(searchParams.get('tab') || ''), 0)
  );
  const [form, setForm] = useState<UpdateCampaignRequest>({});
  const [questionDrafts, setQuestionDrafts] = useState<QuestionDraft[]>([]);
  const [rubricRows, setRubricRows] = useState<RubricRow[]>([]);

  // Seed the form once the campaign arrives.
  useEffect(() => {
    if (!campaign) return;
    setForm({
      name: campaign.name,
      role_title: campaign.role_title,
      location: campaign.location || '',
      description: campaign.description || '',
      hiring_goal_per_month: campaign.hiring_goal_per_month,
      welcome_message_template: campaign.welcome_message_template || '',
      qualification_message_template: campaign.qualification_message_template || '',
      scheduling_message_template: campaign.scheduling_message_template || '',
      scheduling_team: campaign.scheduling_team || null,
    });
    setRubricRows(rubricToRows(campaign.scoring_rubric));
  }, [campaign]);

  useEffect(() => {
    if (!questions) return;
    setQuestionDrafts(
      questions.map((question) => ({
        key: question.id,
        id: question.id,
        question_text: question.question_text,
        question_type: question.question_type,
        options: question.options || [],
        is_knockout: question.is_knockout,
        knockout_answer: question.knockout_answer || '',
      }))
    );
  }, [questions]);

  const updateField = <K extends keyof UpdateCampaignRequest>(
    field: K,
    value: UpdateCampaignRequest[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateQuestion = (key: string, changes: Partial<QuestionDraft>) => {
    setQuestionDrafts((prev) =>
      prev.map((question) => (question.key === key ? { ...question, ...changes } : question))
    );
  };

  const handleAddQuestion = () => {
    setQuestionDrafts((prev) => [
      ...prev,
      {
        key: newKey(),
        question_text: '',
        question_type: 'text',
        options: [],
        is_knockout: false,
        knockout_answer: '',
      },
    ]);
  };

  const handleRemoveQuestion = (key: string) => {
    setQuestionDrafts((prev) => prev.filter((question) => question.key !== key));
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    setQuestionDrafts((prev) => {
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  /**
   * Changing the type invalidates the answer configured for the old type, so drop
   * the knockout answer (and the options list when it no longer applies).
   */
  const handleQuestionTypeChange = (key: string, question_type: QuestionType) => {
    updateQuestion(key, {
      question_type,
      knockout_answer: '',
      options: question_type === 'multiple_choice'
        ? questionDrafts.find((q) => q.key === key)?.options || []
        : [],
    });
  };

  const handleSave = async () => {
    if (!campaignId) return;

    if (!form.name?.trim() || !form.role_title?.trim()) {
      setActiveTab(0);
      toast.error('Campaign name and role title are required');
      return;
    }

    const blankQuestion = questionDrafts.findIndex((q) => !q.question_text.trim());
    if (blankQuestion !== -1) {
      setActiveTab(1);
      toast.error(`Question ${blankQuestion + 1} needs question text`);
      return;
    }

    try {
      await updateCampaign.mutateAsync({
        id: campaignId,
        data: { ...form, scoring_rubric: rowsToRubric(rubricRows) },
      });
      // The bulk endpoint takes array position as the order and deletes any
      // question missing from the payload, so always send the complete list.
      await updateQuestions.mutateAsync({
        campaignId,
        questions: questionDrafts.map((question) => ({
          ...(question.id ? { id: question.id } : {}),
          question_text: question.question_text,
          question_type: question.question_type,
          options: question.options,
          is_knockout: question.is_knockout,
          knockout_answer: question.knockout_answer,
        })),
      });
      toast.success('Campaign saved');
      navigate(`/hr/recruiting/${campaignId}`);
    } catch {
      toast.error('Failed to save campaign');
    }
  };

  if (isLoading || questionsLoading || !campaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSaving = updateCampaign.isPending || updateQuestions.isPending;

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <IconButton onClick={() => navigate(`/hr/recruiting/${campaignId}`)}>
            <ArrowBackOutlined />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
                Edit Campaign
              </Typography>
              <Chip size="sm" variant="soft" color={statusColors[campaign.status]}>
                {statusLabels[campaign.status]}
              </Chip>
              <PageHelpButton />
            </Box>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              {campaign.name}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
          <Button variant="outlined" color="neutral" onClick={() => navigate(`/hr/recruiting/${campaignId}`)}>
            Cancel
          </Button>
          {activeTab !== 5 && <Button startDecorator={<SaveOutlined />} onClick={handleSave} loading={isSaving}>
            Save Changes
          </Button>}
        </Stack>
      </Stack>

      <Card>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
          <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } } }}>
            <Tab>
              <WorkOutlined style={{ marginRight: 8 }} />
              Job Post
            </Tab>
            <Tab>
              <QuestionsOutlined style={{ marginRight: 8 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Screening&nbsp;</Box>Questions
            </Tab>
            <Tab>
              <SmsOutlined style={{ marginRight: 8 }} />
              Messaging
            </Tab>
            <Tab>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Scheduling
            </Tab>
            <Tab>
              <ScoreOutlined style={{ marginRight: 8 }} />
              Resume review
            </Tab>
            <Tab><QuestionsOutlined style={{marginRight: 8}} />Assessment setup</Tab>
          </TabList>

          {/* Job Post */}
          <TabPanel value={0}>
            <Box sx={{ maxWidth: 700 }}>
              <Typography level="title-md" sx={{ mb: 2 }}>Job Post</Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                The role title, location and description appear on the public apply page at
                /apply/{campaign.slug}.
              </Typography>

              <FormControl required sx={{ mb: 2 }}>
                <FormLabel>Campaign Name</FormLabel>
                <Input
                  value={form.name ?? ''}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Q1 Roofing Crew Expansion"
                />
              </FormControl>

              <FormControl required sx={{ mb: 2 }}>
                <FormLabel>Role Title</FormLabel>
                <Input
                  value={form.role_title ?? ''}
                  onChange={(e) => updateField('role_title', e.target.value)}
                  placeholder="e.g., Roofing Technician"
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Location</FormLabel>
                <Input
                  value={form.location ?? ''}
                  onChange={(e) => updateField('location', e.target.value)}
                  placeholder="e.g., Dallas, TX"
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Description &amp; Requirements</FormLabel>
                <Textarea
                  minRows={6}
                  value={form.description ?? ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe the role, responsibilities and the requirements applicants must meet..."
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  Also given to the AI scorer as the position it is grading candidates against.
                </Typography>
              </FormControl>

              <FormControl>
                <FormLabel>Monthly Hiring Goal</FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { min: 1 } }}
                  value={form.hiring_goal_per_month ?? 1}
                  onChange={(e) => updateField('hiring_goal_per_month', parseInt(e.target.value) || 1)}
                  sx={{ maxWidth: 120 }}
                />
              </FormControl>
            </Box>
          </TabPanel>

          {/* Screening Questions */}
          <TabPanel value={1}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography level="title-md">Screening Questions</Typography>
              <Button size="sm" variant="outlined" color="primary" startDecorator={<AddOutlined />} onClick={handleAddQuestion}>
                Add Question
              </Button>
            </Box>

            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              Asked on the application form, while the candidate is still on the page. Answer
              all of them without hitting a knockout and the candidate is qualified
              automatically; a knockout answer is flagged for you to review rather than
              rejecting anyone.
            </Typography>

            <ScreeningQuestionBank questions={questionDrafts} onLoad={rows => setQuestionDrafts(rows.map(row => ({ ...row, key: crypto.randomUUID() })))} />
            {questionDrafts.length === 0 ? (
              <Card variant="soft">
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography level="body-lg" sx={{ mb: 1 }}>No screening questions yet</Typography>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    Without questions candidates stay in Applied until someone moves them by hand.
                  </Typography>
                  <Button startDecorator={<AddOutlined />} onClick={handleAddQuestion}>
                    Add Your First Question
                  </Button>
                </Box>
              </Card>
            ) : (
              questionDrafts.map((question, index) => (
                <Card key={question.key} variant="outlined" sx={{ mb: 1.5 }}>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="sm"
                          variant="plain"
                          disabled={index === 0}
                          onClick={() => handleMoveQuestion(index, 'up')}
                          aria-label="Move question up"
                        >
                          <ArrowUpward size={18} />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          disabled={index === questionDrafts.length - 1}
                          onClick={() => handleMoveQuestion(index, 'down')}
                          aria-label="Move question down"
                        >
                          <ArrowDownward size={18} />
                        </IconButton>
                      </Box>

                      <Chip size="sm" variant="soft" color="neutral" sx={{ mt: 0.5 }}>
                        Q{index + 1}
                      </Chip>

                      <FormControl sx={{ flex: 1 }}>
                        <Textarea
                          size="sm"
                          minRows={2}
                          placeholder="e.g., How many years of roofing experience do you have?"
                          value={question.question_text}
                          onChange={(e) => updateQuestion(question.key, { question_text: e.target.value })}
                        />
                      </FormControl>

                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleRemoveQuestion(question.key)}
                        aria-label="Delete question"
                      >
                        <DeleteOutlined size={18} />
                      </IconButton>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pl: { md: 6 } }}>
                      <FormControl sx={{ minWidth: 180 }}>
                        <FormLabel>Answer Type</FormLabel>
                        <Select
                          size="sm"
                          value={question.question_type}
                          onChange={(_, value) =>
                            value && handleQuestionTypeChange(question.key, value as QuestionType)
                          }
                        >
                          {(Object.keys(questionTypeLabels) as QuestionType[]).map((type) => (
                            <Option key={type} value={type}>
                              {questionTypeLabels[type]}
                            </Option>
                          ))}
                        </Select>
                      </FormControl>

                      {question.question_type === 'multiple_choice' && (
                        <FormControl sx={{ flex: 1 }}>
                          <FormLabel>Options</FormLabel>
                          <Input
                            size="sm"
                            placeholder="Comma separated, e.g., Yes, No, Sometimes"
                            value={question.options.join(', ')}
                            onChange={(e) =>
                              updateQuestion(question.key, {
                                options: e.target.value
                                  .split(',')
                                  .map((option) => option.trim())
                                  .filter(Boolean),
                              })
                            }
                          />
                          <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                            Candidates reply with the option number.
                          </Typography>
                        </FormControl>
                      )}

                      <FormControl>
                        <FormLabel>Knockout</FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 32 }}>
                          <Switch
                            size="sm"
                            checked={question.is_knockout}
                            onChange={(e) =>
                              updateQuestion(question.key, {
                                is_knockout: e.target.checked,
                                knockout_answer: e.target.checked ? question.knockout_answer : '',
                              })
                            }
                          />
                        </Box>
                      </FormControl>

                      {question.is_knockout && (
                        <FormControl sx={{ minWidth: 200 }}>
                          <FormLabel>Disqualifying Answer</FormLabel>
                          {question.question_type === 'yes_no' ? (
                            <Select
                              size="sm"
                              placeholder="Select an answer"
                              value={question.knockout_answer || null}
                              onChange={(_, value) =>
                                updateQuestion(question.key, { knockout_answer: (value as string) || '' })
                              }
                            >
                              <Option value="yes">Yes</Option>
                              <Option value="no">No</Option>
                            </Select>
                          ) : question.question_type === 'multiple_choice' ? (
                            <Select
                              size="sm"
                              placeholder="Select an option"
                              value={question.knockout_answer || null}
                              onChange={(_, value) =>
                                updateQuestion(question.key, { knockout_answer: (value as string) || '' })
                              }
                            >
                              {question.options.map((option) => (
                                <Option key={option} value={option}>
                                  {option}
                                </Option>
                              ))}
                            </Select>
                          ) : (
                            <Input
                              size="sm"
                              type={question.question_type === 'number' ? 'number' : 'text'}
                              placeholder={question.question_type === 'number' ? 'e.g., 0' : 'e.g., none'}
                              value={question.knockout_answer}
                              onChange={(e) =>
                                updateQuestion(question.key, { knockout_answer: e.target.value })
                              }
                            />
                          )}
                          <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                            {question.question_type === 'text'
                              ? 'Free-text answers are interpreted by AI against this answer.'
                              : question.question_type === 'number'
                                ? 'Matched on exact equality — thresholds are not supported.'
                                : 'A candidate who gives this answer is held for review.'}
                          </Typography>
                        </FormControl>
                      )}
                    </Stack>
                  </Box>
                </Card>
              ))
            )}
          </TabPanel>

          {/* Messaging */}
          <TabPanel value={2}>
            <Box sx={{ maxWidth: 700 }}>
              <Typography level="title-md" sx={{ mb: 1 }}>SMS Templates</Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Leave a template blank to use the built-in default. Available placeholders:{' '}
                {'{first_name}'}, {'{last_name}'}, {'{full_name}'}, {'{role_title}'}, {'{location}'},{' '}
                {'{company_name}'}.
              </Typography>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Welcome Message</FormLabel>
                <Textarea
                  minRows={3}
                  value={form.welcome_message_template ?? ''}
                  onChange={(e) => updateField('welcome_message_template', e.target.value)}
                  placeholder="Hi {first_name}! Thanks for applying to the {role_title} position at {company_name}. We received your application and will be in touch soon. Reply STOP to opt out."
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  Sent within minutes of an application. Keep the opt-out sentence.
                </Typography>
              </FormControl>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Qualification Intro</FormLabel>
                <Textarea
                  minRows={3}
                  value={form.qualification_message_template ?? ''}
                  onChange={(e) => updateField('qualification_message_template', e.target.value)}
                  placeholder="A few quick questions to see if this is a fit..."
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  Screening questions are texted using their own wording, so this only sets the tone.
                </Typography>
              </FormControl>

              <FormControl>
                <FormLabel>Scheduling Message</FormLabel>
                <Textarea
                  minRows={3}
                  value={form.scheduling_message_template ?? ''}
                  onChange={(e) => updateField('scheduling_message_template', e.target.value)}
                  placeholder="Great news, {first_name}! You're moving forward in our hiring process. Please schedule your interview here: {booking_link}"
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  Must include {'{booking_link}'} — that placeholder is replaced with the
                  candidate&apos;s personal booking URL.
                </Typography>
              </FormControl>
            </Box>
          </TabPanel>

          {/* Scheduling */}
          <TabPanel value={3}>
            <Box sx={{ maxWidth: 700 }}>
              <Typography level="title-md" sx={{ mb: 1 }}>Interview Scheduling</Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Qualified candidates book themselves onto this team&apos;s round-robin calendar.
                Without a team, &quot;Send Scheduling Link&quot; has no booking link to send and fails.
              </Typography>

              <FormControl>
                <FormLabel>Scheduling Team</FormLabel>
                <Select
                  placeholder="No team selected"
                  value={form.scheduling_team ?? null}
                  onChange={(_, value) => updateField('scheduling_team', (value as string) || null)}
                  sx={{ maxWidth: 360 }}
                >
                  {teams.map((team) => (
                    <Option key={team.id} value={team.id}>
                      {team.name}
                      {team.is_active === false ? ' (inactive)' : ''}
                    </Option>
                  ))}
                </Select>
                {teams.length === 0 && (
                  <Typography level="body-xs" color="warning" sx={{ mt: 1 }}>
                    No scheduling teams exist yet. Create one under Scheduling → Team Scheduling.
                  </Typography>
                )}
              </FormControl>

              {form.scheduling_team && (
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  sx={{ mt: 1 }}
                  onClick={() => updateField('scheduling_team', null)}
                >
                  Clear selection
                </Button>
              )}
            </Box>
          </TabPanel>

          {/* Scoring */}
          <TabPanel value={4}>
            <Box sx={{ maxWidth: 900 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                  mb: 1,
                }}
              >
                <Typography level="title-md">Scoring Rubric</Typography>
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                  {rubricRows.length === 0 && (
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => setRubricRows(DEFAULT_RUBRIC.map((row) => ({ ...row, key: newKey() })))}
                    >
                      Use Default Rubric
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<AddOutlined />}
                    onClick={() =>
                      setRubricRows((prev) => [...prev, { key: newKey(), name: '', weight: 1, description: '' }])
                    }
                  >
                    Add Criterion
                  </Button>
                </Stack>
              </Box>

              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Criteria the AI weighs when it scores a candidate 0-100 from their resume and
                screening answers for applications without an assessment. Assessment applicants use
                their assessment score instead. Leave this rubric empty to use the built-in job-skills default.
              </Typography>

              {rubricRows.map((row, index) => (
                <Card key={row.key} variant="outlined" sx={{ mb: 1.5 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ p: 2, alignItems: { md: 'flex-end' } }}
                  >
                    <FormControl sx={{ minWidth: 200 }}>
                      <FormLabel>Criterion</FormLabel>
                      <Input
                        size="sm"
                        placeholder="e.g., Relevant experience"
                        value={row.name}
                        onChange={(e) =>
                          setRubricRows((prev) =>
                            prev.map((r, i) => (i === index ? { ...r, name: e.target.value } : r))
                          )
                        }
                      />
                    </FormControl>

                    <FormControl sx={{ maxWidth: 100 }}>
                      <FormLabel>Weight</FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        slotProps={{ input: { min: 1 } }}
                        value={row.weight}
                        onChange={(e) =>
                          setRubricRows((prev) =>
                            prev.map((r, i) =>
                              i === index ? { ...r, weight: parseInt(e.target.value) || 1 } : r
                            )
                          )
                        }
                      />
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>What to look for</FormLabel>
                      <Input
                        size="sm"
                        placeholder="e.g., Previous roofing or construction experience"
                        value={row.description}
                        onChange={(e) =>
                          setRubricRows((prev) =>
                            prev.map((r, i) => (i === index ? { ...r, description: e.target.value } : r))
                          )
                        }
                      />
                    </FormControl>

                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => setRubricRows((prev) => prev.filter((_, i) => i !== index))}
                      aria-label="Delete criterion"
                    >
                      <DeleteOutlined size={18} />
                    </IconButton>
                  </Stack>
                </Card>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography level="body-xs" color="neutral">
                Criteria left without a name are dropped when you save.
              </Typography>
            </Box>
          </TabPanel>
          <TabPanel value={5}>{id && <AssessmentLibrary campaignId={id} />}</TabPanel>
        </Tabs>
      </Card>
    </Box>
  );
};

export default CampaignEditPage;
